import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { redeemPromoCode } from 'store/bonusSlice';
import { useGetBonusesQuery, claimBonusLocal } from 'store/bonusesApi';
import { fetchCurrentUser } from 'store/authSlice';
import { showToast } from 'store/toastSlice';
import axiosInstance from 'api/axios';
import { generateIdempotencyKey } from 'utils/idempotency';
import { Bonus } from 'types/bonus';

export type { Bonus };

const useBonus = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { error } = useAppSelector((state) => state.bonus);

  // RTK Query hook with 7-minute polling (420000ms), only active if authenticated
  const {
    data: bonuses = [],
    isLoading: loading,
    isFetching,
    error: bonusesError,
    refetch: refetchQuery,
  } = useGetBonusesQuery(undefined, {
    pollingInterval: isAuthenticated ? 7 * 60 * 1000 : 0,
  });

  const handleRedeemCode = async (code: string) => {
    const resultAction = await dispatch(redeemPromoCode(code));
    if (redeemPromoCode.fulfilled.match(resultAction)) {
      refetchQuery();
      return { success: true, data: resultAction.payload || true };
    }
    return { success: false, error: resultAction.payload as string };
  };

  const handleClaimBonus = async (
    bonusType: string,
    payload?: { gameId: number; betLevel: number },
    idempotencyKey?: string,
  ) => {
    const key = idempotencyKey || generateIdempotencyKey();
    try {
      const res = await axiosInstance.post(
        '/client/levels/bonuses/claim',
        {
          bonusType,
          currency: 'USD',
          ...payload,
        },
        {
          headers: {
            'Idempotency-Key': key,
          },
        },
      );
      if (res.status === 200 || res.status === 201) {
        dispatch(claimBonusLocal(bonusType));
        dispatch(fetchCurrentUser());
        dispatch(
          showToast({
            text: t('rewards.bonuses.claimSuccess', 'Bonus claimed successfully!'),
            type: 'success',
          }),
        );
        return { success: true };
      }
      return { success: false };
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || t('rewards.bonuses.claimError', 'Failed to claim bonus');
      dispatch(showToast({ text: errorMessage, type: 'error' }));
      return { success: false, error: errorMessage };
    }
  };

  const refetch = async () => {
    return await refetchQuery();
  };

  return {
    bonuses,
    loading,
    isFetching,
    error, // Legacy promo error
    bonusesError,
    redeemPromoCode: handleRedeemCode,
    claimBonus: handleClaimBonus,
    refetch,
  };
};

export default useBonus;
